import {TtdRateType} from '../target/target.service';

export interface DistributionAccountSearchModel {
  id: number;
  description: string;
}

export interface Selected {
  selected?: boolean;
}

export interface AccountSegmentMapping {
  segmentId?: number;
  ttdCategoryId?: string;
  ttdCompliance?: string;
  ttdDisplayName?: string;
  ttdPricing?: TtdPriceModel[];
}

// fields from AccountSegmentMapping are copied to object from firstShare
export interface DistributionAccount extends AccountSegmentMapping, Selected {
  id: number;
  accountId?: number;
  description: string;
  userProvidedForeignId: boolean;
  foreignIdForCurrentSegment: string;
  apiId: ApiId;
  firstShare?: AccountSegmentMapping;
  visibleForAll: boolean;
  partnerId: number;
  targetSystemId: number;
  foreignAccountId: string;
}

export class DistributionAccountModel implements DistributionAccount {
  public id!: number;
  public partnerId!: number;
  public foreignAccountId!: string;
  public description!: string;
  public targetSystemId!: number;
  public segmentId!: number;
  public foreignIdForCurrentSegment!: string;
  public userProvidedForeignId!: boolean;
  public selected!: boolean;
  public apiId!: ApiId;
  public ttdCategoryId!: string;
  public ttdPricing!: TtdPriceModel[];
  public visibleForAll!: boolean;

  constructor(fields: DistributionAccount) {
    // we need to rename accountId to foreignAccountId
    if (typeof fields.accountId !== 'undefined') {
      Object.defineProperty(fields, 'foreignAccountId', Object.getOwnPropertyDescriptor(fields, 'accountId') as number);
      delete fields.accountId;
    }
    Object.assign(this, fields);
  }

  static from(object: any): DistributionAccountModel {
    if (object.apiId !== ApiId.DEFAULT && object.shares && object.shares.length > 0) {
      // todo domain models must be fixed - hack flatten
      delete object.shares[0].id;
      Object.assign(object, object.shares[0]);
    }
    return new DistributionAccountModel(object);
  }

  static fromList(objects: any[]): DistributionAccountModel[] {
    return objects.map((item) => {
      return DistributionAccountModel.from(item);
    });
  }

  select() {
    this.selected = true;
  }
}

interface SegmentMappingInterface {
  segmentId: number;
  foreignId: string;
}

export class SegmentMapping implements SegmentMappingInterface {
  public segmentId!: number;
  public foreignId = '';

  constructor(fields?: SegmentMappingInterface) {
    Object.assign(this, fields);
  }
}


export type AccountWithMappings = {
  accountId: string;
  apiId: string;
  description: string;
  id: number;
  partnerId: number;
  segmentMappings: MappedSegment[];
  targetSystemId: number;
  targetSystemName: string;
  userProvidedForeignId: boolean;
  visibleForAll: boolean;
  selected: boolean;
};

export type MappedSegment = {
  activeAgentMappings: any[];
  foreignId: string;
  id: number;
  segmentId: number;
};

interface DistributionAccountCreateRequestInterface {
  partnerId: number;
  foreignAccountId?: string;
  description: string;
  targetSystemId: number;
  initialSegmentMapping: SegmentMapping;
  visibleForAll: boolean;
}

export class DistributionAccountCreateRequestModel implements DistributionAccountEditRequestModel {
  public partnerId!: number;
  public foreignAccountId?: string;
  public description = '';
  public targetSystemId!: number;
  public initialSegmentMapping: SegmentMapping = new SegmentMapping();
  public visibleForAll = false;

  constructor(fields?: DistributionAccountCreateRequestInterface) {
    Object.assign(this, fields);
    this.initialSegmentMapping = new SegmentMapping(fields?.initialSegmentMapping);
  }
}

interface DistributionAccountEditRequestInterface {
  partnerId: number;
  description: string;
}

export class DistributionAccountEditRequestModel implements DistributionAccountEditRequestInterface {
  public partnerId!: number;
  public description!: string;

  constructor(fields?: DistributionAccountEditRequestInterface) {
    Object.assign(this, fields);
  }
}

export type TargetSystemTTD = 9;
export type TargetSystemTTDDataAlliance = 75;
export type TargetSystemGoogleDdp = 11;
export type TargetSystemXandr = 15;
export type TargetSystemActiveAgent = 25;
export type TargetSystemXandrCrtt = 30;
export type TargetSystemPubmatic = 50;
export type TargetSystemPubmaticCrtt = 55;

export const TargetSystemId = {
  GOOGLE_DDP: 11,
  TTD: 9,
  TTD_DATA_ALLIANCE: 75,
  XANDR: 15,
  ACTIVE_AGENT: 25,
  XANDR_CRTT: 30,
  PUBMATIC: 50,
  PUBMATIC_CRTT: 55
};

export enum ApiId {
  DEFAULT = 'DEFAULT',
  TTD = 'TTD',
  ACTIVE_AGENT = 'ACTIVE_AGENT',
  XANDR = 'XANDR',
  GOOGLE = 'GOOGLE',
  ADFORM = 'ADFORM',
  XANDR_CRTT = 'XANDR_CRTT',
  PUBMATIC = 'PUBMATIC',
  PUBMATIC_CRTT = 'PUBMATIC_CRTT',
  TTD_DATA_ALLIANCE = 'TTD_DATA_ALLIANCE',
}

export const ApiIdsWithNoDelete = [ApiId.GOOGLE, ApiId.TTD, ApiId.PUBMATIC, ApiId.PUBMATIC_CRTT, ApiId.TTD_DATA_ALLIANCE];

interface TtdPriceModelInterface {
  buyerId?: string;
  cpm?: number;
  percentOfMediaCostRate?: number;
  type: TtdRateType;
  status?: string;
  lastUpdated?: string;
}

export class TtdPriceModel implements TtdPriceModelInterface {
  buyerId?: string;
  groupId?: number;
  cpm?: number;
  percentOfMediaCostRate?: number;
  type!: TtdRateType;
  status?: string;
  lastUpdated?: string;
  brandId?: string;
}
